/**
 * Unified invoice/estimate totals when invoice-level (order-level) discount interacts with tax.
 * Price book / line pricing is applied to unit prices before calling this.
 */
export var InvoiceDiscountTaxMode;
(function (InvoiceDiscountTaxMode) {
    /** Tax computed on taxable lines at full pre-discount extension; total = subtotal - discount + tax */
    InvoiceDiscountTaxMode["TAX_BEFORE_INVOICE_DISCOUNT"] = "tax_before_invoice_discount";
    /** Discount reduces subtotal first; tax applies to taxable share of post-discount amount */
    InvoiceDiscountTaxMode["TAX_AFTER_INVOICE_DISCOUNT"] = "tax_after_invoice_discount";
})(InvoiceDiscountTaxMode || (InvoiceDiscountTaxMode = {}));
export const DEFAULT_INVOICE_DISCOUNT_TAX_MODE = InvoiceDiscountTaxMode.TAX_BEFORE_INVOICE_DISCOUNT;
/** Contribution of a line to the taxable subtotal used for tax (not the document subtotal). */
function taxableExtensionForTax(line) {
    const ext = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
    // Negative lines (store coupons / line discounts): always adjust taxable base unless
    // manufacturer rebate. Price book often marks coupons isTaxable=false meaning "no tax on
    // the coupon line itself" — we still must apply the negative to the base for store coupons.
    if (ext < 0) {
        if (line.isManufacturerDiscount === true) {
            return 0;
        }
        return ext;
    }
    if (line.isTaxable === false)
        return 0;
    return ext;
}
function roundMoney(n) {
    return Number(n.toFixed(2));
}
function normalizeTaxRateDecimal(rate) {
    if (rate == null || Number.isNaN(rate))
        return 0;
    return rate > 1 ? rate / 100 : rate;
}
/**
 * Compute subtotal, discount, tax, and grand total for a document with line items.
 */
export function calculateDocumentTotals(input) {
    const lines = input.lines || [];
    const subtotal = roundMoney(lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0));
    const taxableSubtotal = roundMoney(lines.reduce((s, l) => s + taxableExtensionForTax(l), 0));
    const dt = input.discountType || 'none';
    let discountAmount = 0;
    if (dt === 'percentage' && input.discountRate != null && Number(input.discountRate) > 0) {
        discountAmount = roundMoney(Math.min(subtotal * (Number(input.discountRate) / 100), subtotal));
    }
    else if (dt === 'fixed' && input.discountRate != null && Number(input.discountRate) > 0) {
        discountAmount = roundMoney(Math.min(Number(input.discountRate), subtotal));
    }
    const rate = normalizeTaxRateDecimal(Number(input.taxRateDecimal) || 0);
    if (input.mode === InvoiceDiscountTaxMode.TAX_BEFORE_INVOICE_DISCOUNT) {
        const taxableBaseForTax = taxableSubtotal;
        const taxAmount = roundMoney(taxableBaseForTax * rate);
        const total = roundMoney(subtotal - discountAmount + taxAmount);
        return {
            subtotal,
            taxableSubtotal,
            discountAmount,
            taxableBaseForTax,
            taxAmount,
            total,
        };
    }
    // TAX_AFTER_INVOICE_DISCOUNT
    const afterDiscount = roundMoney(Math.max(0, subtotal - discountAmount));
    let taxableBaseForTax = 0;
    if (subtotal <= 0) {
        taxableBaseForTax = 0;
    }
    else {
        taxableBaseForTax = roundMoney(taxableSubtotal * (afterDiscount / subtotal));
    }
    const taxAmount = roundMoney(taxableBaseForTax * rate);
    const total = roundMoney(afterDiscount + taxAmount);
    return {
        subtotal,
        taxableSubtotal,
        discountAmount,
        taxableBaseForTax,
        taxAmount,
        total,
    };
}
export function parseInvoiceDiscountTaxMode(value) {
    if (value === InvoiceDiscountTaxMode.TAX_AFTER_INVOICE_DISCOUNT) {
        return InvoiceDiscountTaxMode.TAX_AFTER_INVOICE_DISCOUNT;
    }
    return DEFAULT_INVOICE_DISCOUNT_TAX_MODE;
}
//# sourceMappingURL=documentTotals.js.map